import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiCheckCircle, FiSend } from 'react-icons/fi';

const LOCAL_MODE = false;
function apiUrl(path: string) {
    return LOCAL_MODE ? `http://localhost:8000${path}` : path;
}
function credentials(): RequestCredentials {
    return LOCAL_MODE ? 'include' : 'same-origin';
}

const TELEGRAM_BOT_USERNAME = 'fnn_support_bot';

const fetchCsrfToken = async () => {
    const res = await fetch(apiUrl('/api/csrf-token/'), { credentials: credentials() });
    return res.ok ? (await res.json()).csrfToken : null;
};

export default function TelegramConnect() {
    const [connected, setConnected] = useState<boolean | null>(null);
    const [chatId, setChatId] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const fetchStatus = async () => {
        try {
            const res = await fetch(apiUrl('/portal/api/telegram/link/'), { credentials: credentials() });
            if (res.ok) {
                setConnected((await res.json()).telegram_connected);
            } else {
                setConnected(false);
            }
        } catch {
            setConnected(false);
        }
    };

    useEffect(() => { fetchStatus(); }, []);

    const handleSubmit = async () => {
        const trimmed = chatId.trim();
        if (!trimmed) {
            toast.error('Paste your Chat ID first.');
            return;
        }
        setSubmitting(true);
        try {
            const csrfToken = await fetchCsrfToken();
            if (!csrfToken) { toast.error('CSRF error'); return; }

            const res = await fetch(apiUrl('/portal/api/telegram/link/'), {
                method: 'PATCH',
                credentials: credentials(),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({ chat_id: trimmed }),
            });

            if (res.ok) {
                toast.success('Telegram connected!');
                setConnected(true);
                setChatId('');
            } else {
                const data = await res.json().catch(() => ({}));
                toast.error(data.detail || 'Could not link Telegram.');
            }
        } catch {
            toast.error('Could not link Telegram.');
        } finally {
            setSubmitting(false);
        }
    };

    if (connected === null) return null;

    if (connected) {
        return (
            <div className="telegram-connected-badge">
                <FiCheckCircle className="telegram-verified-icon" />
                <span>Telegram Connected</span>
            </div>
        );
    }

    return (
        <div className="telegram-connect-box">
            <a
                href={`https://t.me/${TELEGRAM_BOT_USERNAME}`}
                target="_blank"
                rel="noreferrer"
                className="btn-primary telegram-connect-btn"
            >
                <FiSend size={13} /> Connect to Telegram
            </a>

            <p className="telegram-connect-hint">
                Open the chat, send <code>/start</code>, then paste the Chat ID the bot gives you below.
            </p>

            <div className="telegram-chatid-row">
                <input
                    type="text"
                    placeholder="Paste your Chat ID…"
                    value={chatId}
                    onChange={e => setChatId(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                />
                <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Linking…' : 'Link'}
                </button>
            </div>
        </div>
    );
}